<template>
  <div class="min-h-screen" data-theme="indigo">
    <NavBar :signed-in="signedIn" :user-name="userName" active-page="split_calculator" />
    <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 class="text-2xl font-bold text-gray-900 mb-6">{{ t('split.title') }}</h1>

      <!-- Input -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('split.distance') }}</label>
            <select v-model="selectedDistance" class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option v-for="d in distances" :key="d.value" :value="d.value">{{ d.label }}</option>
              <option value="custom">{{ t('split.custom') }}</option>
            </select>
          </div>
          <div v-if="selectedDistance === 'custom'">
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('split.custom_distance') }} (m)</label>
            <input v-model.number="customDistance" type="number" min="1" placeholder="1500"
                   class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('split.time') }} (hh:mm:ss)</label>
            <div class="flex gap-1">
              <input v-model="hours" type="number" min="0" max="99" placeholder="00"
                     class="w-16 px-2 py-2 border border-gray-300 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <span class="self-center text-gray-500 font-bold">:</span>
              <input v-model="minutes" type="number" min="0" max="59" placeholder="00"
                     class="w-16 px-2 py-2 border border-gray-300 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <span class="self-center text-gray-500 font-bold">:</span>
              <input v-model="seconds" type="number" min="0" max="59" placeholder="00"
                     class="w-16 px-2 py-2 border border-gray-300 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
        </div>

        <div v-if="pace" class="mt-4 p-3 bg-blue-50 rounded-lg">
          <span class="text-sm font-medium text-blue-800">{{ t('split.pace') }}: {{ pace }} {{ t('split.min_km') }}</span>
          <span class="text-sm text-blue-600 ml-4">{{ speedKmh }} {{ t('split.km_h') }}</span>
        </div>
      </div>

      <!-- Results table -->
      <div v-if="totalSeconds > 0" class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{{ t('split.distance') }}</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{{ t('split.time') }}</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{{ t('split.pace') }}</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="split in splits" :key="split.distance"
                :class="split.distance === inputDistanceMeters ? 'bg-blue-50 font-semibold' : 'hover:bg-gray-50'">
              <td class="px-6 py-3 text-sm text-gray-900">{{ split.label }}</td>
              <td class="px-6 py-3 text-sm text-gray-900">{{ split.time }}</td>
              <td class="px-6 py-3 text-sm text-gray-500">{{ split.pace }}</td>
            </tr>
            <!-- Custom row -->
            <tr class="bg-gray-50">
              <td class="px-6 py-3">
                <div class="flex items-center gap-1">
                  <input v-model.number="tableCustomDistance" type="number" min="1" placeholder="1500"
                         class="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <span class="text-xs text-gray-500">m</span>
                </div>
              </td>
              <td class="px-6 py-3 text-sm text-gray-900">{{ tableCustomTime }}</td>
              <td class="px-6 py-3 text-sm text-gray-500">{{ tableCustomPace }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import NavBar from './NavBar.vue'

const props = defineProps({ signedIn: Boolean, userName: String })
const { t, locale } = useI18n()
const showMenu = ref(false)
const logoSize = ref(120)

const distances = [
  { value: 100, label: '100 m' },
  { value: 200, label: '200 m' },
  { value: 400, label: '400 m' },
  { value: 1000, label: '1 000 m (1 km)' },
  { value: 5000, label: '5 000 m (5 km)' },
  { value: 10000, label: '10 000 m (10 km)' },
  { value: 21097.5, label: '21 097.5 m (Half Marathon)' },
  { value: 42195, label: '42 195 m (Marathon)' }
]

const selectedDistance = ref(10000)
const customDistance = ref(null)
const hours = ref(0)
const minutes = ref(45)
const seconds = ref(0)
const tableCustomDistance = ref(null)

const inputDistanceMeters = computed(() => {
  return selectedDistance.value === 'custom' ? (customDistance.value || 0) : selectedDistance.value
})

const totalSeconds = computed(() => {
  return (parseInt(hours.value) || 0) * 3600 + (parseInt(minutes.value) || 0) * 60 + (parseInt(seconds.value) || 0)
})

const pace = computed(() => {
  if (!totalSeconds.value || !inputDistanceMeters.value) return null
  const paceSeconds = totalSeconds.value / (inputDistanceMeters.value / 1000)
  const m = Math.floor(paceSeconds / 60)
  const s = Math.floor(paceSeconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
})

const speedKmh = computed(() => {
  if (!totalSeconds.value || !inputDistanceMeters.value) return null
  const kmh = (inputDistanceMeters.value / 1000) / (totalSeconds.value / 3600)
  return kmh.toFixed(2) + ' km/h'
})

const splits = computed(() => {
  if (!totalSeconds.value || !inputDistanceMeters.value) return []

  const speedMps = inputDistanceMeters.value / totalSeconds.value
  const allDistances = [...distances.map(d => d.value)]

  // Add custom if used and not already in list
  if (selectedDistance.value === 'custom' && customDistance.value && !allDistances.includes(customDistance.value)) {
    allDistances.push(customDistance.value)
  }

  // Make sure input distance is included
  if (!allDistances.includes(inputDistanceMeters.value)) {
    allDistances.push(inputDistanceMeters.value)
  }

  allDistances.sort((a, b) => a - b)

  return allDistances.map(d => {
    const time = d / speedMps
    const paceS = time / (d / 1000)
    const pM = Math.floor(paceS / 60)
    const pS = Math.floor(paceS % 60)

    return {
      distance: d,
      label: formatDistance(d),
      time: formatTime(time),
      pace: `${pM}:${pS.toString().padStart(2, '0')} /km`
    }
  })
})

const tableCustomTime = computed(() => {
  if (!tableCustomDistance.value || !totalSeconds.value || !inputDistanceMeters.value) return '—'
  const speedMps = inputDistanceMeters.value / totalSeconds.value
  return formatTime(tableCustomDistance.value / speedMps)
})

const tableCustomPace = computed(() => {
  if (!tableCustomDistance.value || !totalSeconds.value || !inputDistanceMeters.value) return '—'
  const speedMps = inputDistanceMeters.value / totalSeconds.value
  const time = tableCustomDistance.value / speedMps
  const paceS = time / (tableCustomDistance.value / 1000)
  const m = Math.floor(paceS / 60)
  const s = Math.floor(paceS % 60)
  return `${m}:${s.toString().padStart(2, '0')} /km`
})

function formatDistance(meters) {
  if (meters === 42195) return '42.195 km (Marathon)'
  if (meters === 21097.5) return '21.097 km (Half Marathon)'
  if (meters >= 1000) return (meters / 1000).toFixed(meters % 1000 === 0 ? 0 : 3) + ' km'
  return meters + ' m'
}

function formatTime(totalSec) {
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = Math.floor(totalSec % 60)
  const ms = Math.floor((totalSec % 1) * 100)

  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
  if (m > 0) return `${m}:${s.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`
  return `${s}.${ms.toString().padStart(2, '0')}s`
}
</script>
